import { useEffect, useMemo, useRef, useState } from "react";

import type { Campaign } from "../../api/generated";

const LOAD_MORE_THRESHOLD_PX = 120;

export type UseCampaignPickerControllerParams = {
  campaigns: Campaign[];
  onSelect: (campaign: Campaign | null) => void;
  onCreate: (name: string) => Promise<Campaign>;
  onCreateSuccess?: (campaign: Campaign) => void;
  search?: string;
  onSearchChange?: (query: string) => void;
  isSearchLoading?: boolean;
  isLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  /** Called after a successful select or create-then-select, so each renderer can close its own shell (dropdown vs. sheet). */
  onCommitted?: () => void;
  /**
   * Caps the campaign name query/create value. Enforced here rather than only
   * via the input's native `maxLength` attribute: a controlled `search` value
   * comes from the caller's own state (not from typing into this input), so the
   * DOM attribute never sees — and can't truncate — it, and `handleCreate` can
   * in principle be invoked directly with an over-limit name.
   */
  maxLength?: number;
};

export type CampaignPickerController = {
  query: string;
  trimmedQuery: string;
  isSearchControlled: boolean;
  filteredCampaigns: Campaign[];
  showCreateRow: boolean;
  isCreating: boolean;
  isMasked: boolean;
  handleQueryChange: (nextQuery: string) => void;
  resetQuery: () => void;
  handleSelect: (campaign: Campaign | null) => void;
  handleCreate: (name: string) => Promise<void>;
  handleResultsScroll: (event: React.UIEvent<HTMLDivElement>) => void;
};

export const useCampaignPickerController = ({
  campaigns,
  onSelect,
  onCreate,
  onCreateSuccess,
  search,
  onSearchChange,
  isSearchLoading = false,
  isLoading = false,
  hasNextPage = false,
  onLoadMore,
  onCommitted,
  maxLength,
}: UseCampaignPickerControllerParams): CampaignPickerController => {
  const loadMoreRequestedRef = useRef(false);

  const [internalQuery, setInternalQuery] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const clamp = (value: string) =>
    maxLength !== undefined ? value.slice(0, maxLength) : value;

  const isSearchControlled = search !== undefined;
  const query = clamp(isSearchControlled ? (search ?? "") : internalQuery);
  const trimmedQuery = query.trim();

  const filteredCampaigns = useMemo(
    () =>
      isSearchControlled
        ? campaigns
        : campaigns.filter((campaign) =>
            campaign.name.toLowerCase().includes(trimmedQuery.toLowerCase()),
          ),
    [campaigns, isSearchControlled, trimmedQuery],
  );

  const hasExactMatch = campaigns.some(
    (campaign) => campaign.name.toLowerCase() === trimmedQuery.toLowerCase(),
  );
  const showCreateRow = trimmedQuery !== "" && !hasExactMatch;

  const isMasked = isLoading || isSearchLoading;

  const canLoadMore =
    hasNextPage &&
    onLoadMore !== undefined &&
    (isSearchControlled || trimmedQuery === "");

  useEffect(() => {
    loadMoreRequestedRef.current = false;
  }, [campaigns.length, trimmedQuery]);

  const handleResultsScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!canLoadMore) return;
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight > LOAD_MORE_THRESHOLD_PX) {
      return;
    }
    // Latches until `campaigns` grows — a single wheel gesture fires many scroll events before `isLoadingMore` updates.
    if (loadMoreRequestedRef.current) return;
    loadMoreRequestedRef.current = true;
    onLoadMore?.();
  };

  const resetQuery = () => {
    if (!isSearchControlled) setInternalQuery("");
    onSearchChange?.("");
  };

  const handleQueryChange = (nextQuery: string) => {
    const clamped = clamp(nextQuery);
    if (!isSearchControlled) setInternalQuery(clamped);
    onSearchChange?.(clamped);
  };

  const handleSelect = (campaign: Campaign | null) => {
    if (isCreating) return;
    onSelect(campaign);
    onCommitted?.();
    if (!isSearchControlled) setInternalQuery("");
  };

  const handleCreate = async (name: string) => {
    setIsCreating(true);
    try {
      const created = await onCreate(clamp(name));
      onSelect(created);
      onCreateSuccess?.(created);
      onCommitted?.();
      if (!isSearchControlled) setInternalQuery("");
    } catch {
      // Swallow: `createError` is the caller's responsibility; avoids an unhandled rejection.
    } finally {
      setIsCreating(false);
    }
  };

  return {
    query,
    trimmedQuery,
    isSearchControlled,
    filteredCampaigns,
    showCreateRow,
    isCreating,
    isMasked,
    handleQueryChange,
    resetQuery,
    handleSelect,
    handleCreate,
    handleResultsScroll,
  };
};
